import React, { useCallback, useEffect, useState } from 'react';
import {
  Card, Upload, Button, Space, List, Tag, Tooltip, Typography, Empty, message, Popconfirm, Pagination, Spin,
} from 'antd';
import type { UploadFile, RcFile } from 'antd/es/upload/interface';
import { InboxOutlined, FileOutlined, DeleteOutlined, DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import {
  listCustomerFiles,
  uploadCustomerFiles,
  deleteCustomerFile,
  downloadCustomerFileBlob,
  type CustomerFileDTO,
  type Page as CustomerFilesPage,
} from '../../services/customers';

import {
  listActivityFiles,
  uploadActivityFiles,
  deleteActivityFile,
  downloadActivityFileBlob,
  type ActivityFileDTO,
  type Page as ActivityFilesPage,
} from '../../services/activities';

const { Dragger } = Upload;
const { Text } = Typography;

type EntityKind = 'customer' | 'activity';

type AnyFileDTO = (CustomerFileDTO | ActivityFileDTO) & {
  id: number;
  filename: string;
  url?: string | null;
  contentType?: string | null;
  sizeBytes?: number | null;
  uploadedAt?: string | null;
  uploadedById?: number | null;
};

type Props = {
  entity: EntityKind;
  entityId: number;
  readOnly?: boolean;
  title?: string;
  pageSize?: number;
  accept?: string;
  onUploaded?: () => void;
  onDeleted?: () => void;
  style?: React.CSSProperties;
  className?: string;
};

function humanSize(n?: number | null) {
  if (n == null) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(1)} GB`;
}

function iconByType(_: string | null | undefined) {
  return <FileOutlined />;
}

const FileManager: React.FC<Props> = ({
  entity,
  entityId,
  readOnly = false,
  title = 'Allegati',
  pageSize = 10,
  accept,
  onUploaded,
  onDeleted,
  style,
  className,
}) => {
  const [page, setPage] = useState(1); // 1-based UI
  const [items, setItems] = useState<AnyFileDTO[]>([]);
  const [total, setTotal] = useState(0);
  const [size, setSize] = useState(pageSize);
  const [loading, setLoading] = useState(false);

  const [pendingFiles, setPendingFiles] = useState<UploadFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const isCustomer = entity === 'customer';

  const fetchList = useCallback(
    async (p = page, s = size) => {
      if (!entityId) return;
      setLoading(true);
      try {
        if (isCustomer) {
          const data: CustomerFilesPage<CustomerFileDTO> =
            await listCustomerFiles(entityId, p - 1, s);
          setItems((data.content || []) as AnyFileDTO[]);
          setTotal(data.totalElements || 0);
        } else {
          const data: ActivityFilesPage<ActivityFileDTO> =
            await listActivityFiles(entityId, p - 1, s);
          setItems((data.content || []) as AnyFileDTO[]);
          setTotal(data.totalElements || 0);
        }
      } catch (e: any) {
        if (String(e?.message || '').includes('404') && !isCustomer) {
          setItems([]);
          setTotal(0);
        } else {
          message.error(e?.message || 'Impossibile caricare gli allegati');
        }
      } finally {
        setLoading(false);
      }
    },
    [entityId, isCustomer, page, size]
  );

  useEffect(() => {
    if (!entityId) return;
    setPage(1);
    fetchList(1, size);
  }, [entityId, entity, size, fetchList]);

  const handleRemove = async (fileId: number) => {
    try {
      if (isCustomer) {
        await deleteCustomerFile(entityId, fileId);
      } else {
        await deleteActivityFile(entityId, fileId);
      }
      message.success('File eliminato');
      onDeleted?.();
      fetchList(page, size);
    } catch (e: any) {
      message.error(e?.message || 'Impossibile eliminare il file');
    }
  };

  const doUpload = async () => {
    const files = pendingFiles.map(f => f.originFileObj).filter(Boolean) as RcFile[];
    if (!files.length) {
      message.info('Seleziona almeno un file');
      return;
    }

    setUploading(true);
    try {
      if (isCustomer) {
        await uploadCustomerFiles(entityId, files);
      } else {
        await uploadActivityFiles(entityId, files);
      }
      message.success('Upload completato');
      setPendingFiles([]);
      onUploaded?.();
      fetchList(page, size);
    } catch (e: any) {
      message.error(e?.message || 'Errore durante l’upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (file: AnyFileDTO) => {
    try {
      const blob = isCustomer
        ? await downloadCustomerFileBlob(entityId, file.id)
        : await downloadActivityFileBlob(entityId, file.id);

      const url = window.URL.createObjectURL(blob);
      try {
        const a = document.createElement('a');
        a.href = url;
        a.download = file.filename || 'file';
        document.body.appendChild(a);
        a.click();
        a.remove();
      } finally {
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (e: any) {
      message.error(e?.message || 'Download fallito');
    }
  };

  return (
    <Card
      title={title}
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchList(page, size)}>
            Ricarica
          </Button>
          {!readOnly && (
            <Button
              type="primary"
              loading={uploading}
              disabled={!pendingFiles.length}
              onClick={doUpload}
            >
              Carica {pendingFiles.length ? `(${pendingFiles.length})` : ''}
            </Button>
          )}
        </Space>
      }
      style={style}
      className={className}
    >
      {!readOnly && (
        <Dragger
          multiple
          accept={accept}
          beforeUpload={() => false}
          fileList={pendingFiles}
          onChange={({ fileList }) => setPendingFiles(fileList)}
          style={{ borderRadius: 12, marginBottom: 16 }}
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Trascina qui i file o clicca per selezionare</p>
          <p className="ant-upload-hint">I file verranno caricati quando premi “Carica”.</p>
        </Dragger>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : items.length === 0 ? (
        <Empty description="Nessun allegato" />
      ) : (
        <>
          <List
            itemLayout="horizontal"
            dataSource={items}
            renderItem={(f) => (
              <List.Item
                actions={[
                  <Tooltip key="open" title="Apri / Scarica">
                    <Button type="text" icon={<DownloadOutlined />} onClick={() => handleDownload(f)} />
                  </Tooltip>,
                  !readOnly && (
                    <Popconfirm
                      key="del"
                      title="Elimina file?"
                      okText="Elimina"
                      okButtonProps={{ danger: true }}
                      onConfirm={() => handleRemove(f.id)}
                    >
                      <Button type="text" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  ),
                ].filter(Boolean as any)}
              >
                <List.Item.Meta
                  avatar={iconByType(f.contentType)}
                  title={
                    <Space size={8} wrap>
                      <Text strong>{f.filename}</Text>
                      <Tag>{humanSize(f.sizeBytes)}</Tag>
                    </Space>
                  }
                  description={
                    <Space split={<span>•</span>} wrap>
                      <span>
                        {f.uploadedAt
                          ? `Caricato il ${dayjs(f.uploadedAt).format('DD/MM/YYYY HH:mm')}`
                          : 'Data sconosciuta'}
                      </span>
                      
                    </Space>
                  }
                />
              </List.Item>
            )}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <Pagination
              size="small"
              current={page}
              pageSize={size}
              total={total}
              showSizeChanger
              onChange={(p, s) => {
                setPage(p);
                setSize(s);
                fetchList(p, s);
              }}
              showTotal={(t, r) => `${r[0]}-${r[1]} di ${t} file`}
            />
          </div>
        </>
      )}
    </Card>
  );
};

export default FileManager;
